import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useMemo, useState } from 'react';
import { toggleFacetQuery } from '~/features/facets/util/toggleFacetQuery';
import { toggleManyFacetQueries } from '~/features/facets/util/toggleManyFacetQueries';
import { useProductListPagination } from '~/features/pagination/hooks/useProductListPagination';
import { IProductListPagination } from '~/features/pagination/models/pagination';
import { useProductFilterTracking } from '~/features/tracking/hooks/useProductFilterTracking';
import { useProductSortingTracking } from '~/features/tracking/hooks/useProductSortingTracking';
import { IFacet, IFacetValue, IFilter, IM170ProductFilterModule, ISelectedFacetValue, ISorting, ISortOption } from '~/lib/data-contract';
import { useGetRequest } from '~/shared/utils/request/hooks/useGetRequest';
import { resetPagination } from '~/features/pagination/utils/resetPagination';
import { buildURL } from '~/shared/utils/request/utils/buildURL';
import { fetcher } from '~/shared/utils/request/utils/fetcher';
import { IProductFilterData, IProductFilterExpand } from '~/templates/blocks/components/M170ProductFilter/models/M170ProductFilter';
import { applyRouteFacetQuery } from '../context/reducers/applyRouteFacetQuery';
import { useProductFilterExpandState } from '../context/useProductFilterExpandState';

const findById = (id: string) => (item: IFacet | IFacetValue | ISortOption) => item.id === id;

export const useProductFilterData = (initialFilter: IFilter, id: string): { filter: IProductFilterData; pagination: IProductListPagination; filterExpandState: IProductFilterExpand } => {
	const router = useRouter();
	const routerQuery = router.query;
	const routerAsPath = router.asPath;

	const trackProductFilter = useProductFilterTracking();
	const trackProductSorting = useProductSortingTracking();

	const [previousData, setPreviousData] = useState(initialFilter);
	const [initialRoute, setInitialRoute] = useState<string | null>(routerAsPath);

	const productFilterExpandState = useProductFilterExpandState();

	const { data: { filter: queryFilter } = {}, isLoading } = useGetRequest<IM170ProductFilterModule>('/api/proxy/content/module', {
		params: {
			url: routerAsPath,
			moduleId: id,
		},
		enabled: routerAsPath !== initialRoute,
	});
	const queryDataOrPrevious = queryFilter || previousData;

	const { facets, sorting, page }: IFilter = useMemo(() => {
		return applyRouteFacetQuery(queryDataOrPrevious, routerQuery);
	}, [queryDataOrPrevious, routerQuery]);

	const fetchUpdatedFilter = async (routerQuery: ParsedUrlQuery): Promise<IFilter> => {
		const { slug, ...queryParams } = routerQuery;
		const fetchUrl = buildURL('/api/proxy/content/module', {
			moduleId: id,
			url: location.pathname,
			...queryParams,
		});

		const request = fetcher<IM170ProductFilterModule>(fetchUrl);
		const requestData = await request();

		return requestData.filter;
	};

	const selectedSort = router.query[sorting.queryParameter] as string | undefined;

	// TODO Try to extract for usage directly on M170
	const productListPagination = useProductListPagination(page);

	useEffect(() => {
		if (queryFilter && queryFilter !== previousData) {
			setPreviousData(queryFilter);
			setInitialRoute(null); // Once we navigate away the initial route is not relevant anymore
		}
	}, [queryFilter]);

	const selectedFacets: ISelectedFacetValue[] = useMemo(() => {
		// Maps all selected facetValues to an array with an extra property facetId that references the containing group
		return facets
			.map((facet) => facet.values.map((facetValue) => ({ ...facetValue, facetId: facet.id })))
			.flat()
			.filter((facet) => facet.selected);
	}, [facets]);

	return useMemo(() => {
		const updateSort = (sort: string, toggleSortingDropdown?: boolean, updateUrl?: boolean): ISorting | void => {
			// Tracking
			if (sort) {
				trackProductSorting(sort);
			}

			if (toggleSortingDropdown) {
				productFilterExpandState.toggleExpandedSorting();
			}

			router.query[sorting.queryParameter] = sort;

			if (updateUrl) {
				return updateFilterUrl();
			}

			return {
				...sorting,
				options: sorting.options.map((item) => ({
					...item,
					selected: item.id === sort,
				})),
			};
		};

		const toggleFacetValue = async (facetId: string, facetValueId: string, updateUrl?: boolean): Promise<IFacet[] | void> => {
			// Tracking
			const facet = facets.find(findById(facetId));
			const facetValue = facet?.values?.find(findById(facetValueId));
			if (facetValue) {
				trackProductFilter(facetValue.label);
			}

			// Updates router query which triggers re-validation
			toggleFacetQuery(router.query, facetId, facetValueId);

			if (updateUrl) {
				return updateFilterUrl();
			}

			const updatedFilter = await fetchUpdatedFilter(router.query);

			return updatedFilter.facets;
		};

		const removeAllFacets = async (updateUrl?: boolean): Promise<IFacet[] | void> => {
			facets.forEach((facet) => {
				delete router.query[facet.id];
			});

			if (updateUrl) {
				return updateFilterUrl();
			}

			const updatedFilter = await fetchUpdatedFilter(router.query);

			return updatedFilter.facets;
		};

		const toggleFacetValueMany = async (values: IFacet[], updateUrl?: boolean): Promise<IFacet[] | void> => {
			// Tracking
			for (const facet of values) {
				for (const facetValue of facet.values) {
					if (facetValue.selected) {
						trackProductFilter(facetValue.label);
					}
				}
			}

			// Updates router query which triggers re-validation
			toggleManyFacetQueries(router.query, values);

			if (updateUrl) {
				return updateFilterUrl();
			}

			const updatedFilter = await fetchUpdatedFilter(router.query);

			return updatedFilter.facets;
		};

		const updateFilterUrl = () => {
			resetPagination(router.query);
			router.replace(router, undefined, { shallow: true });
		};

		return {
			filter: {
				facets,
				selectedFacets,
				toggleFacetValue,
				toggleFacetValueMany,
				removeAllFacets,
				sorting,
				selectedSort,
				updateSort,
				updateFilterUrl,
				isLoading,
			} as IProductFilterData,
			pagination: productListPagination,
			filterExpandState: productFilterExpandState,
		};
	}, [facets, sorting, productListPagination, productFilterExpandState]);
};
